import {useState} from 'react'
import cn from 'clsx'
import {parseISO} from 'date-fns'
import {Space} from 'antd'

import styles from './CashflowMiniStat.module.scss'
import useStore from 'base/store'
import {
  Paper,
  Block,
  Box,
  Text,
  Button,
  Meter,
  Loader,
  UserAvatar,
  IconButton,
  IconStat,
  VerticalDivider,
  ArrowsRoundIcon,
  DownloadDotIcon,
  BlackboardChartIcon,
} from 'common/components'
import {
  formatMoney,
  countPercentage,
  formatDate,
  totalNumberFromArray,
  createReport,
  capitalizeFirstLetter,
  isItemDrawdown,
  maybePluralize,
} from 'common/helpers'
import {
  PREV_WEEK_PERIOD,
  PREV_MONTH_PERIOD,
  PREV_TWO_MONTHS_PERIOD,
  NEXT_WEEK_PERIOD,
  NEXT_MONTH_PERIOD,
  NEXT_TWO_MONTHS_PERIOD,
} from 'common/constants'
import {ForecastType} from 'common/types'
import {
  CashflowPanel,
  PeriodSelect,
  useGetDashboardTransactions,
  useGetDashboardForecast,
  getCurrentPeriod,
  createCustomPeriod,
} from '..'

interface CashflowMiniStatProps {
  name: TName
}

interface ContentProps {
  name: TName
  data: any
  type: ForecastType
  currentPeriod: any
}

interface GetReportDataParams {
  items: any[]
  managers?: any[]
  manager?: any
  projects?: any[]
}

type TName = 'forecast' | 'transactions'

export default function CashflowMiniStat({name}: CashflowMiniStatProps) {
  const [customPeriod, setCustomPeriod] = useState<any[]>()
  const role = useStore((state) => state.role)
  const [type, setType] = useState<ForecastType>('drawdown')
  const isForecast = name === 'forecast'
  const periods = [
    ...(isForecast ? forecastPeriods : transactionsPeriods),
    createCustomPeriod(customPeriod),
  ]
  const [periodKey, setPeriodKey] = useState<string>(periods[0].key)
  const currentPeriod = getCurrentPeriod(periods, periodKey)
  const {data: transactions} = useGetDashboardTransactions(
    currentPeriod.period,
    name === 'transactions'
  )
  const {data: forecast} = useGetDashboardForecast(
    currentPeriod.period,
    isForecast
  )
  const handleTypeChange = () =>
    setType((state) => (state === 'drawdown' ? 'repayment' : 'drawdown'))

  const data = isForecast ? forecast : transactions

  return (
    <Paper>
      <CashflowPanel margin="small" title={name}>
        <PeriodSelect
          periods={periods}
          periodKey={periodKey}
          customPeriod={customPeriod}
          setPeriodKey={setPeriodKey}
          setCustomPeriod={setCustomPeriod}
        />
        <Button variant="secondary" onClick={handleTypeChange}>
          for {type}s
        </Button>
      </CashflowPanel>
      {data ? (
        role.manager ? (
          <ManagerContent
            name={name}
            data={data}
            type={type}
            currentPeriod={currentPeriod}
          />
        ) : (
          <Content
            name={name}
            data={data}
            type={type}
            currentPeriod={currentPeriod}
          />
        )
      ) : (
        <Loader />
      )}
    </Paper>
  )
}

const Content = ({data, name, type, currentPeriod}: ContentProps) => {
  const isForecast = isNameForecast(name)
  const isRepayment = isTypeRepayment(type)
  const typeAmount = Math.abs(
    data[isRepayment ? 'totalRepaymentsAmount' : 'totalDrawdownsAmount']
  )
  const currentData = data[isForecast ? 'forecastItems' : 'transactions']
  const items = currentData[type]
  const managersPercents = data.managers.map((manager: any) => {
    const managerTotalAmount = Math.abs(
      totalNumberFromArray(
        items.filter((item: any) => item.managerUuid === manager.uuid),
        'amount'
      )
    )
    return countPercentage(managerTotalAmount, typeAmount)
  })

  const handleReport = () =>
    createReport(
      getReportData({
        items: [...currentData.drawdown, ...currentData.repayment],
        managers: data.managers,
        projects: data.projects,
      }),
      capitalizeFirstLetter(name),
      `${name}-report_${currentPeriod.period.dateFrom}-${currentPeriod.period.dateTo}`
    )

  return (
    <>
      <ul className={styles.list}>
        {data.managers.map((manager: any, index: number) => (
          <Item
            key={manager.uuid}
            name={name}
            type={type}
            manager={manager}
            items={items.filter(
              (item: any) => item.managerUuid === manager.uuid
            )}
            managersPercents={managersPercents}
            index={index}
            projects={data.projects}
            currentPeriod={currentPeriod}
          />
        ))}
      </ul>
      <Box align="right">
        <Space size={24}>
          <IconStat
            row
            icon={isForecast ? <BlackboardChartIcon /> : <ArrowsRoundIcon />}
            iconColor={isForecast ? 'turquoize' : undefined}
            value={formatMoney(typeAmount)}
            label={`Total for ${currentPeriod.tip}`}
          />
          <Button disabled={!items.length} onClick={handleReport}>
            Create report
          </Button>
        </Space>
      </Box>
    </>
  )
}

const ManagerContent = ({data, name, type, currentPeriod}: ContentProps) => {
  const isForecast = isNameForecast(name)
  const currentData = data[isForecast ? 'forecastItems' : 'transactions']
  const items = currentData[type]

  const handleReport = () =>
    createReport(
      getReportData({
        items,
        manager: data.managers[0],
        projects: data.projects,
      }),
      `${capitalizeFirstLetter(name)} ${type}`,
      `${name}-${type}-report_${currentPeriod.period.dateFrom}-${currentPeriod.period.dateTo}`
    )

  const isRepayment = isTypeRepayment(type)
  const typeAmount = Math.abs(
    totalNumberFromArray(
      data[isForecast ? 'forecastItems' : 'transactions'][type],
      'amount'
    )
  )
  const totalAmount = Math.abs(
    data[isRepayment ? 'totalRepaymentsAmount' : 'totalDrawdownsAmount']
  )
  const diffAmountPercent = countPercentage(typeAmount, totalAmount)

  return (
    <>
      <div className={styles.content}>
        <IconStat
          row
          icon={isForecast ? <BlackboardChartIcon /> : <ArrowsRoundIcon />}
          iconColor={isForecast ? 'turquoize' : undefined}
          value={formatMoney(typeAmount)}
          label={currentPeriod.tip}
        />
        <VerticalDivider margin="small" />
        <div>
          <div className={styles.meterBlock}>
            <Text size="3xl" weight="medium" mr={12}>
              {diffAmountPercent}%
            </Text>
            <Meter
              color={
                isForecast ? 'turquoize' : isRepayment ? 'default' : 'coal'
              }
              width={164}
              value={diffAmountPercent}
            />
          </div>
          <Text lowercase size="xs" color="grey" weight="medium">
            from all {name} for {currentPeriod.label}
          </Text>
        </div>
        <IconButton
          icon={<DownloadDotIcon />}
          disabled={!items.length}
          onClick={handleReport}
        />
      </div>
    </>
  )
}

const Item = ({
  name,
  type,
  manager,
  items,
  managersPercents,
  index,
  projects,
  currentPeriod,
}: any) => {
  const isForecast = isNameForecast(name)
  const totalAmount = Math.abs(totalNumberFromArray(items, 'amount'))
  const projectsCount = isForecast
    ? new Set(items.map((item: any) => item.projectUuid)).size
    : null

  const handleReport = totalAmount
    ? () =>
        createReport(
          getReportData({
            items,
            manager,
            projects,
          }),
          capitalizeFirstLetter(name),
          `${manager.firstName.toLowerCase()}-${name}-${type}-report_${
            currentPeriod.period.dateFrom
          }-${currentPeriod.period.dateTo}`
        )
    : undefined

  return (
    <Block as="li" className={styles.item}>
      <Space>
        <UserAvatar src={manager.photo} size={36} alt={manager.firstName} />
        <Text as="span" weight="medium">
          {manager.firstName}
        </Text>
      </Space>
      <div className={styles.block}>
        <span className={styles.percent}>{managersPercents[index]}%</span>
        <Meters
          activeIndex={index}
          isForecast={isForecast}
          isRepayment={isTypeRepayment(type)}
          percents={managersPercents}
        />
      </div>
      <Space className={styles.total} size={16}>
        <Box align="right">
          <Text weight="medium" color="grey" size="xs" mb={2}>
            {isForecast
              ? maybePluralize(projectsCount, 'project')
              : 'Statements total'}
          </Text>
          <Text size="2xl">{formatMoney(totalAmount)}</Text>
        </Box>
        <IconButton
          icon={<DownloadDotIcon />}
          disabled={!handleReport}
          onClick={handleReport}
        />
      </Space>
    </Block>
  )
}

const Meters = ({isForecast, isRepayment, activeIndex, percents}: any) => (
  <div className={styles.meters}>
    {percents.map((percent: any, index: number) =>
      percent ? (
        <div
          key={index}
          className={cn(
            styles.meter,
            activeIndex === index && styles.meter_active,
            isForecast
              ? styles.meter_color_turquoize
              : isRepayment
              ? styles.meter_color_green
              : styles.meter_color_black
          )}
          style={{width: `${percent}%`}}
        />
      ) : null
    )}
  </div>
)

const isNameForecast = (name: TName): boolean => name === 'forecast'
const isTypeRepayment = (type: ForecastType): boolean => type === 'repayment'

const getReportData = ({
  items,
  managers,
  manager,
  projects,
}: GetReportDataParams): any[] => {
  const sortedItems = items.slice()
  sortedItems.sort((a, b) => +parseISO(a.date) - +parseISO(b.date))
  const months = getItemsMonthsToReport(sortedItems)
  let reportData = sortedItems.map((item) => {
    const amount = isItemDrawdown(item) ? -item.amount : item.amount
    const data: any = {
      LM:
        manager?.firstName ??
        managers.find((manager) => manager.uuid === item.managerUuid)
          ?.firstName,
      Type: item.type,
    }
    if (projects) {
      const project = projects.find(
        (project) => project.uuid === item.projectUuid
      )
      data['Loan\\Project Name'] = project.title
      data['Loan\\Borrower ID'] = project.borrower.email
    }
    data['Date'] = formatDate(item.date, 'third')
    data['Amount'] = amount
    months.forEach((month) => {
      data[month] = formatDate(item.date, 'eleventh') === month ? amount : null
    })
    return data
  })
  const reportTotal: any = {
    Date: 'Net cashflow',
    Amount: getTotalItemsAmount(items),
  }
  months.forEach((month) => {
    const monthItems = items.filter(
      (item) => formatDate(item.date, 'eleventh') === month
    )
    reportTotal[month] = getTotalItemsAmount(monthItems)
  })
  reportData = [...reportData, {}, reportTotal]
  return reportData
}

const getItemsMonthsToReport = (items: any[]): any[] =>
  Array.from(new Set(items.map((item) => formatDate(item.date, 'eleventh'))))

const getTotalItemsAmount = (items: any[]): number =>
  items.reduce(
    (sum, item) => sum + (isItemDrawdown(item) ? -item.amount : item.amount),
    0
  )

const transactionsPeriods = [
  PREV_WEEK_PERIOD,
  PREV_MONTH_PERIOD,
  PREV_TWO_MONTHS_PERIOD,
]

const forecastPeriods = [
  NEXT_WEEK_PERIOD,
  NEXT_MONTH_PERIOD,
  NEXT_TWO_MONTHS_PERIOD,
]
